import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Button, IconButton, Switch, TextField } from "@mui/material";
import { styled as muiStyled } from '@mui/material/styles';
import { Check, Delete, EditOutlined } from '@mui/icons-material';
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import ShareIcon from "@mui/icons-material/Share";
import { Row as Rows } from "antd";
import { map } from "lodash";
import styled from "styled-components";
import { WriteEditWhite } from "utils/assets/images";
import COLOR from "utils/constants/color";
import { v4 as uuid } from "uuid";
import { useDrag, useDrop } from "react-dnd";
import { confirmUnshareWidget } from "../../../store/widgets/confirmUnshare";

// Convert makeStyles to styled components
const StyledTextField = muiStyled(TextField)(({ theme }) => ({
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
  '& input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]::-webkit-inner-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
}));

const StyledRows = muiStyled(Rows)({
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
  gap: '5px',
  marginTop: '52px',
});

const StyledInputField = muiStyled(TextField)(({ windowSize }) => ({
  width: `${windowSize?.width >= 768 ? 124 : 46}px !important`,
  padding: '6px 12px 5px !important',
  color: '#000',
  '& label.Mui-focused': {
    color: 'white',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: 'white',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
  },
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
}));

const StyledUserInput = muiStyled(TextField)({
  marginTop: '8px',
});

const StyledUserInputControlled = muiStyled(TextField)({
  marginTop: '8px',
  color: 'white !important',
  '& .MuiInputBase-root': {
    height: '25px',
    padding: '0 14px',
    '& .MuiInputBase-input': {
      fontSize: '0.875rem',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
  },
  '& .MuiOutlinedInput-root': {
    padding: 0,
    position: 'relative',
  },
});

const isTouchDevice = () => {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

const DraggableUserItem = ({ user, groupIndex, currentUser, groups, setDragging }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'USER',
    item: { userId: user.id, groupIndex },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (!monitor.didDrop()) {
        setDragging(null);
      }
    },
  });

  const MyUser = user?.id === currentUser?.id;

  return (
    <UserItem
      ref={drag}
      type={groups?.length > 0}
      getMyUser={MyUser}
      style={{ 
        cursor: "grab",
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {`${user.userName || ""}`}
    </UserItem>
  );
};

const DroppableGroupArea = ({ groupIndex, children, group, onDrop, expandedGroup }) => {
  const [{ isOver }, drop] = useDrop({
    accept: 'USER',
    drop: (item) => {
      if (item.groupIndex !== groupIndex) {
        onDrop(item, groupIndex);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <UserListArea
      ref={drop}
      type={true}
      key={group.station}
      style={{ 
        borderRadius: 20,
        border: isOver ? "2px dashed #4CAF50" : "none",
        backgroundColor: isOver ? "rgba(76, 175, 80, 0.1)" : "transparent",
      }}
      className={
        expandedGroup === groupIndex ? "expandOnDragOver" : ""
      }
    >
      {children}
    </UserListArea>
  );
};

const UserInputSection = ({ groupIndex, newUser, onUserChange, onAddUser, onToggleInput }) => {
  const firstNameValue = newUser?.firstName || "";
  const lastNameValue = newUser?.lastName || "";
  
  return (
    <div>
      <StyledUserInput
        id={`firstName-${groupIndex}`}
        type="text"
        name="firstName"
        placeholder="First Name"
        className="first-name-color"
        variant="outlined"
        size="small"
        style={{ color: "white" }}
        InputLabelProps={{ shrink: true }}
        inputProps={{ shrink: true }}
        value={firstNameValue}
        onChange={(e) => onUserChange(e, groupIndex)}
      />
      <StyledUserInput
        id={`lastName-${groupIndex}`}
        type="text"
        name="lastName"
        className="last-name-color"
        placeholder="Last Name"
        variant="outlined"
        size="small"
        style={{ color: "white", marginTop: "10px" }}
        InputLabelProps={{ shrink: true }}
        inputProps={{ shrink: true }}
        value={lastNameValue}
        onChange={(e) => onUserChange(e, groupIndex)}
      />
      <Button
        className="checkRghtBtn"
        variant="contained"
        color="primary"
        size="small"
        style={{ marginTop: "10px", backgroundColor: "#03989E"}}
        startIcon={<CheckIcon style={{color:"white"}} />}
        onClick={async () => {
          await onAddUser(groupIndex);
          onToggleInput(groupIndex);
        }}
      />
    </div>
  );
};

const WidgetStationComponent = (props) => {
  const isTouch = useMemo(() => isTouchDevice(), []);
  const {
    shareStatus,
    perGroupStudent,
    generatedGroup,
    currentUser,
    stations,
  } = props?.states || {};
  const {
    onClose,
    resetGroup,
    onChangePerStation,
    handlerShareGroup,
    handleOpenEditModal,
    updateGeneratedGroup,

    handlerGenerateStudentGroup,
    handleToogleEnabledOrDisabled,
    handleUserNewAdded,
    handleDeleteStation,
    handleStationChange,
    handleCreateNewStation,
  } = props?.funcObj || {};

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const [showInput, setShowInput] = useState({});
  const [newUser, setNewUser] = useState({});
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [groups, setGroups] = useState([]);
  const userAreaRef = useRef(null);

  const [editableIndex, setEditableIndex] = useState(-1);
  const [addStations, setAddStations] = useState(false);
  const [newStation, setNewStation] = useState({
    id: "",
    label: "",
    enabled: true,
    custom: true,
  });

  useEffect(() => {
    setGroups(generatedGroup);
  }, [generatedGroup]);

  const handleCloseClick = async () => {
    const shouldClose = await confirmUnshareWidget({
      isShared: shareStatus,
      widgetName: "Station widget",
    });
    if (!shouldClose) return;
    onClose();
  };

  const handleResize = () => {
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleNewUserChange = useCallback((e, groupIndex) => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;
    setNewUser((prev) => ({
      ...prev,
      [groupIndex]: { 
        ...(prev[groupIndex] || {}), 
        [fieldName]: fieldValue 
      },
    }));
  }, []);

  const toggleInput = useCallback((groupIndex) => {
    setShowInput((prev) => {
      const isOpening = !prev[groupIndex];
      if (isOpening) {
        setNewUser((prevUser) => ({
          ...prevUser,
          [groupIndex]: { firstName: "", lastName: "" },
        }));
      }
      return { ...prev, [groupIndex]: isOpening };
    });
  }, []);

  const handleAddNewUserToGroup = useCallback(async (groupIndex) => {
    setNewUser((prevNewUser) => {
      if (prevNewUser[groupIndex]) {
        setGroups((prevGroups) => {
          const updatedGroups = prevGroups.map((group, index) => {
            const newUserData = {
              id: uuid(),
              role: "student",
              userName: `${prevNewUser[groupIndex]?.firstName || ""} ${
                prevNewUser[groupIndex]?.lastName || ""
              }`,
            };
            if (index === groupIndex) {
              return {
                ...group,
                students: [...group.students, newUserData],
              };
            }
            return group;
          });
          handleUserNewAdded(updatedGroups, prevNewUser[groupIndex]);
          return updatedGroups;
        });
        return { ...prevNewUser, [groupIndex]: null };
      }
      return prevNewUser;
    });
  }, [handleUserNewAdded]);

  const handleScroll = (event) => {
    const { clientY } = event;
    const userArea = userAreaRef.current;

    if (!userArea) return;

    const { top, bottom } = userArea.getBoundingClientRect();
    const offset = 10;

    if (clientY - top < offset) {
      userArea.scrollTop -= offset;
    } else if (bottom - clientY < offset) {
      userArea.scrollTop += offset;
    }
  };



  const handleDrop = useCallback(async (item, groupIndex) => {
    try { 
      if (item.groupIndex !== groupIndex) {
        setGroups((prevGroups) => {
          const sourceGroupStudents = prevGroups[item.groupIndex].students.filter(
            (user) => user.id !== item.userId
          );
          const droppedUser = prevGroups[item.groupIndex].students.find(
            (user) => user.id === item.userId
          );
          
          if (droppedUser) {
            const targetGroupStudents = [...prevGroups[groupIndex].students, droppedUser];

            const newGroups = prevGroups.map((group, index) => {
              if (index === item.groupIndex)
                return { ...group, students: sourceGroupStudents };
              if (index === groupIndex)
                return { ...group, students: targetGroupStudents };
              return group;
            });
            updateGeneratedGroup(newGroups);
            return newGroups;
          }
          return prevGroups;
        });
      }
    } catch (error) {
      console.error("Error in handleDrop:", error);
    }
    setExpandedGroup(null);
    setDragging(null);
  }, [updateGeneratedGroup]);

  return (
      <div className="custom-fullscreen-container newStationModal">
        <div className="modal-container-parent">
        <div className="modal-container-new-station addWidgetSpc">
          <div style={{ marginTop: "15px" }} className="heading-label">
            New Station
          </div>
          <div className="modal-inner-container">
            <button
              style={{
                backgroundColor: shareStatus && "green",
              }}
              onClick={handlerShareGroup}
              className="share-button-widget-modal "
            >
              <ShareIcon style={{ color: "white", fontSize: 13 }} /> 
              {!shareStatus ? "Share" : "Shared"}
            </button>
            <div onClick={handleCloseClick} className="cancel-icon-station">
              <CancelIcon />
            </div>
          </div>

        <StyledRows>
          <StyledInputField
            id="noOfGroup"
            name="noOfGroup"
            label={windowSize?.width >= 768 ? "Students per stations" : ""}
            InputLabelProps={{
              shrink: true,
              required: true,
              style: { color: "#000" },
              fontSize: "8px",
            }}
            inputProps={{
              shrink: true,
              required: true,
              style: { color: "#000" },
              fontSize: "8px",
            }}
            type="number"
            variant="outlined"
            onChange={(e) => {
              if (e.target.value >= 0) {
                onChangePerStation(e.target.value);
              }
            }}
            value={perGroupStudent}
            windowSize={windowSize}
            className="studentsPerCstm"
          />
          <button
            style={{ border: "none", background: "none", outline: "none" }}
            onClick={() => {
              setShowInput((prev) =>
                Object.fromEntries(Object.keys(prev).map((key) => [key, false]))
              );
              handlerGenerateStudentGroup();
            }}
          >
            <GenerateListButton>Generate</GenerateListButton>
          </button>

          <button
            onClick={() => {
              setShowInput((prev) =>
                Object.fromEntries(Object.keys(prev).map((key) => [key, false]))
              );
              resetGroup();
            }}
            style={{ border: "none", background: "none", outline: "none" }}
          >
            <ResetButton>Reset</ResetButton>
          </button>

          <button
            onClick={() => handleOpenEditModal(true)}
            style={{ border: "none", background: "none", outline: "none" }}
          >
            <EditListOption>
              Edit <IconsImg src={WriteEditWhite} />
            </EditListOption>
          </button>
        </StyledRows>
        <UserArea
          type={groups?.length > 0}
          dataExist={groups?.length > 0}
          ref={userAreaRef}
          onDragOver={handleScroll}
        >
          {groups?.length > 0 ? (
            groups?.map((group, groupIndex) => (
              <SectionsGroup key={groupIndex}>
                <GroupHeaderText>{group.label || ""}</GroupHeaderText>
                <DroppableGroupArea 
                  groupIndex={groupIndex} 
                  group={group}
                  onDrop={handleDrop}
                  expandedGroup={expandedGroup}
                >
                  {group.students.map((user) => (
                    <DraggableUserItem 
                      key={user.id}
                      user={user} 
                      groupIndex={groupIndex} 
                      currentUser={currentUser}
                      groups={groups}
                      setDragging={setDragging}
                    />
                  ))}
                  {!showInput[groupIndex] && (
                    <IconButton
                      color="primary"
                      style={{ color: COLOR.blueishdeep }}
                      onClick={() => toggleInput(groupIndex)}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                  {showInput[groupIndex] && (
                    <UserInputSection
                      key={`input-section-${groupIndex}`}
                      groupIndex={groupIndex}
                      newUser={newUser[groupIndex]}
                      onUserChange={handleNewUserChange}
                      onAddUser={handleAddNewUserToGroup}
                      onToggleInput={toggleInput}
                    />
                  )}
                </DroppableGroupArea>
              </SectionsGroup>
            ))
          ) : (
            <UserListArea type={false}>
              {map(stations, (station, i) => (
                <UserItem key={station.id} style={{ cursor: "default" }}>
                  {editableIndex === i ? (
                    <StyledUserInputControlled
                      type="text"
                      name="station"
                      placeholder="station"
                      variant="outlined"
                      size="small"
                      autoFocus
                      style={{ color: "white" }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ shrink: true }}
                      value={newStation.label}
                      onChange={(e) => {
                        setNewStation({
                          ...newStation,
                          label: e.target.value,
                        });
                      }}
                    />
                  ) : (
                    station.label
                  )}
                  {editableIndex === i ? (
                    <Check
                      style={{ cursor: "pointer", fontSize: 18 }}
                      onClick={() => {
                        setEditableIndex(-1);
                        if (newStation.label.length > 0) {
                          handleStationChange(station?.id, newStation);
                          setNewStation({
                            label: "",
                            enabled: true,
                            custom: true,
                          });
                        }
                      }}
                    />
                  ) : (
                    <div className="flex items-center">
                      <EditOutlined
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setEditableIndex(i);
                          setNewStation(station);
                          setAddStations(false);
                        }}
                      />
                      {!station.custom && (
                        <Switch
                          checked={station.enabled}
                          size="small"
                          onChange={() =>
                            handleToogleEnabledOrDisabled(station?.id)
                          }
                          name="enabled"
                          color="primary"
                        />
                      )}
                      {station.custom && (
                        <IconButton
                          size="small"
                          color="secondary"
                          style={{ marginLeft: 10, fontSize: 20 }}
                          onClick={() => handleDeleteStation(station?.id)}
                        >
                          <Delete style={{ fontSize: 20 }} />
                        </IconButton>
                      )}
                    </div>
                  )}
                </UserItem>
              ))}
              {!addStations ? (
                <IconButton
                  color="primary"
                  onClick={() => setAddStations(true)}
                >
                  <AddIcon />
                </IconButton>
              ) : (
                <div>
                  <StyledUserInput
                    type="text"
                    name="station"
                    placeholder="station"
                    variant="outlined"
                    size="small"
                    style={{ color: "white" }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ shrink: true }}
                    value={newStation.label}
                    onChange={(e) => {
                      setNewStation({ ...newStation, label: e.target.value });
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ marginTop: "10px", borderRadius: "50%" }}
                    startIcon={<CheckIcon />}
                    onClick={() => {
                      if (newStation.label.length > 0) {
                        handleCreateNewStation(newStation);
                        setNewStation({
                          label: "",
                          enabled: true,
                          custom: true,
                        });
                        setAddStations(false);
                      } else {
                        setAddStations(false);
                      }
                    }}
                  />
                </div>
              )}
            </UserListArea>
          )}
        </UserArea>
      </div>
      <div className="widgetFooterNewStation NewStationFtr"><label>Pop Up</label></div>
      </div>
      
    </div>
  );
};

export default WidgetStationComponent;

const GenerateListButton = styled.div`
  text-align: center;
  align-items: center;
  background-image: linear-gradient(
    90deg,
    ${COLOR.WheelEditColor1} 0%,
    ${COLOR.WheelEditColor2} 40%,
    ${COLOR.WheelEditColor3} 100%
  );
  color: #fff;
  display: flex;
  transition: all 300ms ease;
  white-space: nowrap;
  width: 90px;
  height: 30px;
  border-radius: 10px;
  justify-content: center;
  font-weight: bolder;
  cursor: pointer;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 800px;
  // min-height: 500px;
  // background-color: rgb(58,58,58);
  position: relative;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 0px;
  z-index: 99999999;
  position: fixed;
  cursor: default;
  left: 25%;
  border-radius: 13px;
`;

const LogoTopRow = styled.div``;
const Logo = styled.img`
  height: auto;
  width: 160px;
`;

const CloseBtn = styled.div`
  background-color: ${COLOR.PinkExam};
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  position: absolute;
  top: 10px;
  right: 10px;
  color: white;
  z-index: 1;
`;

const EditListOption = styled.div`
  text-align: center;
  align-items: center;
  background-image: linear-gradient(
    90deg,
    ${COLOR.WheelEditColor1} 0%,
    ${COLOR.WheelEditColor2} 40%,
    ${COLOR.WheelEditColor3} 100%
  );
  color: #fff;
  display: flex;
  transition: all 300ms ease;
  white-space: nowrap;
  width: 100px;
  height: 30px;
  border-radius: 10px;
  justify-content: center;
  font-weight: bolder;
  cursor: pointer;

  @media only screen and (max-width: 768px) {
    width: 80px;
  }
`;

const ResetButton = styled.div`
  text-align: center;
  align-items: center;
  background: #d11919;
  color: white;
  display: flex;
  transition: all 300ms ease;
  white-space: nowrap;
  width: 100px;
  height: 30px;
  border-radius: 10px;
  justify-content: center;
  font-weight: bolder;
  cursor: pointer;
  // @media (max-width: 997px) {
  //   margin-top: 16px;
  // }

  @media only screen and (max-width: 768px) {
    width: 80px;
  }
`;

const UserArea = styled.div`
  display: ${(props) => (props?.dataExist ? "flex" : "")};
  flex-wrap: wrap;
  justify-content: start;
  // flex-direction: row;
  margin-top: 52px;
  // flex-wrap: wrap;
  height: calc(100% - 100px);
  //   place-items: center;

  max-width: ${(props) => (!props?.type ? "443px" : "")};
  // max-height: ${(props) => (!props?.type ? "431px" : "460px")};
  margin-inline: ${(props) => (!props?.type ? "auto" : "")};

  // grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  // overflow: hidden auto !important;
  //   @media (max-width: 997px) {
  //     max-width: 90vw;
  //   }

  ::-webkit-scrollbar {
    width: 3px;
    height: 6px;
  }

  @media only screen and (max-width: 768px) {
    padding: 10px;
  }
`;

const SectionsGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media only screen and (max-width: 768px) {
    width: 100%;
    padding-inline: 8px;
  }
`;

const GroupHeaderText = styled.div`
  background-color: #fd9d1a;
  padding: 8px 10px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  width: 200px;
  text-align: center;
  color: #fff;
  justify-content: center;
  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`;

const UserListArea = styled.div`
  margin-inline: auto;
  background: linear-gradient(
    90deg,
    #df6074 0%,
    #bd57b4 30%,
    #a451e7 50%,
    #9a59e8 60%,
    #7579f0 90%,
    #6786f4 100%
  ) !important;
  min-height: 114px;
  width: ${(props) => (!props?.type ? "" : "200px")};
  border-radius: 10px;
  padding: 10px;
  margin: ${(props) => (props?.type ? "12px 16px" : "")};
  margin-bottom: ${(props) => !props?.type && "10px"};
  // height: ${(props) => (props?.type ? "100%" : "")};

  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`;

const UserItem = styled.div`
  background: ${(p) => (p.getMyUser ? "#152B69" : "#fff")};
  color: ${(p) => (p.getMyUser ? "#fff" : "")};
  padding: 8px 10px;
  border-radius: 8px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: move;
`;

const UserInput = styled(TextField)`
  margin-top: 8px;
`;

const UserInputControled = styled(TextField)`
  margin-top: 8px;
  color: white !important;

  .MuiInputBase-root {
    height: 25px; // Adjust the height as needed
    padding: 0 14px; // Reduce padding to decrease height

    .MuiInputBase-input {
      font-size: 0.875rem; // Adjust font size to match the smaller input
    }
  }

  .MuiInputLabel-root {
    font-size: 0.875rem; // Adjust label font size to match
  }

  .MuiOutlinedInput-root {
    padding: 0;
    position: relative;
  }
`;

const ModalRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`;

const UserListAreaEmpty = styled.div`
  background: #cbbbe7;
  min-height: 114px;
  min-width: 175px;
  border-radius: 10px;
  padding: 10px;
  margin: 12px 16px;
  display: flex;
  align-items: center;
`;

const IconsImg = styled.img`
  height: 15px;
  width: 15px;
  object-fit: cover;
  margin-left: 10px;
`;

const ModalContent = styled.div`
  align-items: center;
  background-color: ${COLOR.white};
  border-radius: 3px;
  display: inline-flex;
  flex-direction: column;
  left: 50%;
  padding: 16px;
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%);
`;